import { DateValue } from '@internationalized/date';
import { TradeAggregateType } from '@rematter/frontend-lib-api-client';
import { atom, useAtom } from 'jotai';

import { tradeOutboxEventCountsQueryAtom } from '../_graphql/queryTradeOutboxEventCounts';
import { tradeOutboxEventsQueryAtom } from '../_graphql/queryTradeOutboxEvents';

import {
  dateToZonedDateTimeEnd,
  dateToZonedDateTimeStart,
} from '~/shared/utils/dateTime';

export type TradeAuditTab = 'events' | 'inbox';

export const $tradeAuditTab = atom<TradeAuditTab>('events');

export const $tradeAggregateTypeFilter = atom<TradeAggregateType | undefined>(
  undefined,
);

export const $tradeAggregateIdFilter = atom<string | undefined>(undefined);

export const $tradePagination = atom<{ offset: number; limit: number }>({
  offset: 0,
  limit: 50,
});

export const $selectedTradeEventId = atom<string | undefined>(undefined);

export const $tradeDateRangeFilter = atom<{
  start: DateValue;
  end: DateValue;
} | null>(null);

export const $remoteTradeOutboxEvents = tradeOutboxEventsQueryAtom((get) => {
  const aggregateType = get($tradeAggregateTypeFilter);
  const aggregateId = get($tradeAggregateIdFilter);
  const pagination = get($tradePagination);
  const dateRange = get($tradeDateRangeFilter);

  return {
    variables: {
      aggregateTypes: aggregateType ? [aggregateType] : undefined,
      aggregateId,
      pagination,
      createdAtRange:
        dateRange?.start && dateRange?.end
          ? {
              start: dateToZonedDateTimeStart(dateRange.start),
              end: dateToZonedDateTimeEnd(dateRange.end),
            }
          : undefined,
    },
  };
});

export const $remoteTradeOutboxEventCounts = tradeOutboxEventCountsQueryAtom();

export const useTradeAuditTab = () => {
  const [tab, setTab] = useAtom($tradeAuditTab);
  return [tab, setTab] as const;
};

export const useTradeAggregateTypeFilter = () => {
  const [filter, setFilter] = useAtom($tradeAggregateTypeFilter);
  const [, setAggregateId] = useAtom($tradeAggregateIdFilter);

  const setFilterAndResetId = (value: TradeAggregateType | undefined) => {
    setFilter(value);
    setAggregateId(undefined);
  };

  return [filter, setFilterAndResetId] as const;
};

export const useTradeAggregateIdFilter = () => {
  const [filter, setFilter] = useAtom($tradeAggregateIdFilter);
  return [filter, setFilter] as const;
};

export const useTradePagination = () => {
  const [pagination, setPagination] = useAtom($tradePagination);
  return [pagination, setPagination] as const;
};

export const useSelectedTradeEventId = () => {
  const [selectedId, setSelectedId] = useAtom($selectedTradeEventId);
  return [selectedId, setSelectedId] as const;
};

export const useTradeDateRangeFilter = () => {
  const [filter, setFilter] = useAtom($tradeDateRangeFilter);
  return [filter, setFilter] as const;
};
